import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, Check, ChevronRight, ChevronLeft, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { MainLayout } from "@/components/layout/MainLayout";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";

// Get gesture options with translations
const getGestureOptions = (t: (key: string) => string) => [
  { id: "blink", label: t("blinkGesture"), shortLabel: t("blinkGestureShort") },
  { id: "look_up", label: t("lookUpGesture"), shortLabel: t("lookUpGestureShort") },
  { id: "look_down", label: t("lookDownGesture"), shortLabel: t("lookDownGestureShort") },
  { id: "look_left", label: t("lookLeftGesture"), shortLabel: t("lookLeftGestureShort") },
  { id: "look_right", label: t("lookRightGesture"), shortLabel: t("lookRightGestureShort") },
];

// Actions for different gesture counts with translations
const getActionsFor5Gestures = (t: (key: string) => string) => [
  { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
  { id: "up", label: t("upAction"), defaultGesture: "look_up" },
  { id: "down", label: t("downAction"), defaultGesture: "look_down" },
  { id: "left", label: t("leftAction"), defaultGesture: "look_left" },
  { id: "right", label: t("rightAction"), defaultGesture: "look_right" },
];

const getActionsFor4Gestures = (t: (key: string) => string) => {
  const language = getUserSettings().language;
  const isRTL = language === 'he' || language === 'ar';
  return [
    { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
    { id: "next", label: t("nextAction"), defaultGesture: isRTL ? "look_left" : "look_right" },
    { id: "previous", label: t("previousAction"), defaultGesture: isRTL ? "look_right" : "look_left" },
    { id: "back", label: t("backAction"), defaultGesture: "look_down" },
  ];
};

const getActionsFor3Gestures = (t: (key: string) => string) => [
  { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
  { id: "next", label: t("nextAction"), defaultGesture: "look_right" },
  { id: "back", label: t("backAction"), defaultGesture: "look_left" },
];

const getActionsFor2Gestures = (t: (key: string) => string) => [
  { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
  { id: "back", label: t("backAction"), defaultGesture: "look_down" },
];

const getActionsFor1Gesture = (t: (key: string) => string) => [
  { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
];

// Get scan mode description
const getScanModeInfo = (gestureCount: number, t: (key: string) => string) => {
  if (gestureCount >= 5) return { title: t("fullDirectionalNav"), subtitle: t("fullDirectionalNavDesc") };
  if (gestureCount === 4) return { title: t("advancedNav"), subtitle: t("advancedNavDesc") };
  if (gestureCount === 3) return { title: t("autoScanWithCancel"), subtitle: t("manualScanNavDesc") };
  if (gestureCount === 2) return { title: t("autoScanWithCancel"), subtitle: t("autoScanWithCancelDesc") };
  return { title: t("autoScanOnly"), subtitle: t("autoScanOnlyDesc") };
};

// Get which actions should be shown based on gesture count
const getActionsForGestureCount = (count: number, t: (key: string) => string) => {
  if (count >= 5) return getActionsFor5Gestures(t);
  if (count === 4) return getActionsFor4Gestures(t);
  if (count === 3) return getActionsFor3Gestures(t);
  if (count === 2) return getActionsFor2Gestures(t);
  return getActionsFor1Gesture(t);
};

interface MappingRowProps {
  actionId: string;
  actionLabel: string;
  selectedGesture: string;
  availableGestures: string[];
  onGestureChange: (gestureId: string) => void;
  isDropdownOpen: boolean;
  onToggleDropdown: () => void;
  gestureOptions: { id: string; label: string; shortLabel: string }[];
  selectGesturePrompt: string;
  isRTL: boolean;
}

// Get the appropriate icon for each action.
// For next/previous, the icon reflects the physical eye-movement direction,
// which is reversed in RTL languages (next = look_left, previous = look_right).
const getActionIcon = (actionId: string, isRTL: boolean = false) => {
  switch (actionId) {
    case "up":
      return ArrowUp;
    case "down":
      return ArrowDown;
    case "left":
      return ArrowLeft;
    case "right":
      return ArrowRight;
    case "next":
      return isRTL ? ArrowLeft : ArrowRight;
    case "previous":
      return isRTL ? ArrowRight : ArrowLeft;
    case "back":
      return ArrowLeft;
    case "select":
    case "cancel":
      return Check;
    default:
      return ArrowLeft;
  }
};

function MappingRow({ 
  actionId,
  actionLabel, 
  selectedGesture, 
  availableGestures,
  onGestureChange, 
  isDropdownOpen,
  onToggleDropdown,
  gestureOptions,
  selectGesturePrompt,
  isRTL
}: MappingRowProps) {
  const currentGesture = gestureOptions.find(g => g.id === selectedGesture);
  const availableGestureOptions = gestureOptions.filter(g => 
    availableGestures.includes(g.id)
  );
  
  const ActionIcon = getActionIcon(actionId, isRTL);
  
  return (
    <div className="relative">
      <div className={cn(
        "w-full flex items-center gap-3 p-3 rounded-xl",
        "bg-card border-2 border-primary/20",
        "transition-all duration-200"
      )}>
        {/* Action name - fixed width for alignment */}
        <span className={cn(
          "text-sm font-medium w-[110px] text-primary flex-shrink-0 whitespace-nowrap",
          isRTL ? "text-right" : "text-left"
        )}>
          {actionLabel}
        </span>
        
        {/* Direction icon - fixed width column for vertical alignment */}
        <div className="w-7 flex items-center justify-center flex-shrink-0">
          <ActionIcon className="w-4 h-4 text-primary" />
        </div>
        
        {/* Gesture dropdown button */}
        <div className="flex-1 flex justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleDropdown();
            }}
            className={cn(
              "flex items-center justify-between gap-2 px-3 py-2 rounded-xl w-36",
              "bg-primary/20 hover:bg-primary/30 text-foreground font-medium",
              "transition-all duration-200 border border-primary/30",
              isDropdownOpen && "ring-2 ring-primary bg-primary/30"
            )}
          >
            <ChevronDown className={cn(
              "w-4 h-4 transition-transform text-primary",
              isDropdownOpen && "rotate-180"
            )} />
            <span className="text-right flex-1 text-sm">{currentGesture?.shortLabel || selectGesturePrompt}</span>
          </button>
        </div>
      </div>
      
      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="absolute left-4 right-4 top-full mt-1 z-50 bg-card rounded-xl shadow-lg border-2 border-primary/20 overflow-hidden">
          {availableGestureOptions.map(gesture => (
            <button
              key={gesture.id}
              onClick={(e) => {
                e.stopPropagation();
                onGestureChange(gesture.id);
              }}
              className={cn(
                "w-full px-3 py-2 text-right text-sm hover:bg-primary/10 transition-colors",
                selectedGesture === gesture.id && "bg-primary text-primary-foreground hover:bg-primary"
              )}
            >
              {gesture.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ActionMapping() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  const [selectedGestures, setSelectedGestures] = useState<string[]>([]);
  const [actionMapping, setActionMapping] = useState<Record<string, string>>({});
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const gestureOptions = getGestureOptions(t);

  // Load saved settings
  useEffect(() => {
    const settings = getUserSettings();
    const gestures = settings.eyeControlGestures || [];
    setSelectedGestures(gestures);
    
    // Create default mapping based on selected gestures ONLY
    const visibleActions = getActionsForGestureCount(gestures.length, t);
    const defaultMapping: Record<string, string> = {};
    const usedGestures: string[] = [];
    
    visibleActions.forEach((action, index) => {
      // First try the default gesture if it's in the selected list and not used
      if (gestures.includes(action.defaultGesture) && !usedGestures.includes(action.defaultGesture)) {
        defaultMapping[action.id] = action.defaultGesture;
        usedGestures.push(action.defaultGesture);
      } else {
        // Find first available gesture that hasn't been used yet
        const availableGesture = gestures.find((g: string) => !usedGestures.includes(g));
        if (availableGesture) {
          defaultMapping[action.id] = availableGesture;
          usedGestures.push(availableGesture);
        } else {
          // Fallback to the gesture at this index (shouldn't happen if counts match)
          defaultMapping[action.id] = gestures[index % gestures.length];
        }
      }
    });
    
    // Use existing mapping if available and valid (all mapped gestures are still selected)
    const existingMapping = settings.actionMapping;
    let finalMapping = defaultMapping;
    if (existingMapping && Object.keys(existingMapping).length > 0) {
      // Validate existing mapping - all gestures must be in selected list
      const isValid = Object.values(existingMapping).every((g: unknown) => gestures.includes(g as string));
      if (isValid) {
        finalMapping = { ...defaultMapping, ...existingMapping };
      }
    }

    // Resolve gesture conflicts: two actions using the same gesture — keep first, re-assign second
    const visibleActionIds = visibleActions.map(a => a.id);
    const seenGestures: Record<string, string> = {}; // gesture → first actionId that uses it
    const resolvedMapping = { ...finalMapping };
    for (const actionId of visibleActionIds) {
      const gesture = resolvedMapping[actionId];
      if (!gesture) continue;
      if (seenGestures[gesture]) {
        // Conflict — find an available gesture not yet used
        const usedSoFar = Object.values(resolvedMapping);
        const available = gestures.find(g => !usedSoFar.includes(g));
        if (available) {
          resolvedMapping[actionId] = available;
        }
      } else {
        seenGestures[gesture] = actionId;
      }
    }

    // Only keep entries for currently visible actions — drop stale entries from
    // previous gesture counts (e.g. 'right', 'left', 'up', 'down' from 5-gesture
    // mode left over when user switches to 4-gesture mode). Stale entries in the
    // saved actionMapping can cause non-configured gestures to trigger actions.
    const cleanedMapping: Record<string, string> = {};
    for (const actionId of visibleActionIds) {
      cleanedMapping[actionId] = resolvedMapping[actionId];
    }

    setActionMapping(cleanedMapping);
  }, [t]);

  const handleBack = () => {
    navigate("/control/eye-control");
  };

  const handleContinue = () => {
    // Save action mapping — only include entries for currently visible actions
    // to avoid stale entries from previous gesture counts causing gesture fallthrough.
    const cleanMapping: Record<string, string> = {};
    visibleActions.forEach(action => {
      if (actionMapping[action.id]) cleanMapping[action.id] = actionMapping[action.id];
    });
    const settings = getUserSettings();
    saveUserSettings({
      ...settings,
      actionMapping: cleanMapping,
      controlMethod: "eye_control"
    });
    
    // Store in sessionStorage for InputMethodContext
    sessionStorage.setItem('controlMethod', 'eye_control');
    
    // Store gesture count for scanning config
    localStorage.setItem('ayuni_scanning_config', JSON.stringify({
      pattern: settings.scanMode === 'row_and_column' ? 'ROW_COLUMN' : 'LINEAR',
      speed: (settings.scanSpeed || 3) * 1000,
      skipEmpty: true,
      inputCount: selectedGestures.length
    }));
    
    // Dispatch events to notify context
    window.dispatchEvent(new CustomEvent('controlMethodChanged', { 
      detail: { method: 'eye_control' } 
    }));
    window.dispatchEvent(new CustomEvent('scanningConfigChanged', { 
      detail: { inputCount: selectedGestures.length } 
    }));
    
    navigate("/");
  };

  const handleGestureChange = (actionId: string, newGestureId: string) => {
    setActionMapping(prev => {
      const currentGestureOfAction = prev[actionId];
      
      // Find if another action is using the new gesture
      const otherActionWithNewGesture = Object.entries(prev).find(
        ([otherActionId, gestureId]) => otherActionId !== actionId && gestureId === newGestureId
      );
      
      if (otherActionWithNewGesture) {
        // Swap: give the other action our current gesture
        return {
          ...prev,
          [actionId]: newGestureId,
          [otherActionWithNewGesture[0]]: currentGestureOfAction
        };
      } else {
        // No swap needed
        return {
          ...prev,
          [actionId]: newGestureId
        };
      }
    });
    setOpenDropdown(null);
  };

  const toggleDropdown = (actionId: string) => {
    setOpenDropdown(prev => prev === actionId ? null : actionId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const visibleActions = getActionsForGestureCount(selectedGestures.length, t);
  const modeInfo = getScanModeInfo(selectedGestures.length, t);

  const settings = getUserSettings();
  const isFemale = settings.gender === "female";

  return (
    <MainLayout>
      <div 
        className="flex-1 flex flex-col items-center p-6 bg-background overflow-auto"
        dir={dir}
      >
        {/* Header with icon */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg">
            <Settings className="w-6 h-6 text-primary-foreground" />
          </div>
          <h1 className="text-xl font-bold text-foreground">
            {t("actionMappingTitle")}
          </h1>
        </div>

        {/* Subtitle */}
        <p className="text-sm text-muted-foreground text-center mb-3">
          {isFemale ? t("clickGestureToChangeFemale") : t("clickGestureToChangeMale")}
        </p>

        {/* Mapping rows */}
        <div className="w-full max-w-xl space-y-3 mb-6">
          {visibleActions.map(action => (
            <MappingRow
              key={action.id}
              actionId={action.id}
              actionLabel={action.label}
              selectedGesture={actionMapping[action.id] || ""}
              availableGestures={selectedGestures}
              onGestureChange={(gestureId) => handleGestureChange(action.id, gestureId)}
              isDropdownOpen={openDropdown === action.id}
              onToggleDropdown={() => toggleDropdown(action.id)}
              gestureOptions={gestureOptions}
              selectGesturePrompt={t("selectGesturePrompt")}
              isRTL={isRTL}
            />
          ))}
        </div>

        {/* Selected Mode Info */}
        <div className="w-full max-w-xl bg-card border-2 border-primary/20 rounded-2xl p-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-primary font-bold">{modeInfo.title}</span>
            <span className="text-muted-foreground text-sm">{t("selectedMode")}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1 text-right">
            {modeInfo.subtitle}
          </p>
        </div>

        {/* Action buttons - Back on left, Save on right */}
        <div className="flex gap-3 mt-auto pb-4">
          <button
            onClick={handleBack}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-card hover:bg-muted border-2 border-border",
              "text-muted-foreground hover:text-foreground"
            )}
          >
            {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            <span>{t("back")}</span>
          </button>

          <button
            onClick={handleContinue}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
            )}
          >
            <Check className="w-5 h-5" />
            <span>{t("save")}</span>
          </button>
        </div>
      </div>
    </MainLayout>
  );
}
